/**
 * Life4Earth V2 — Admin Onboarding API layer
 *
 * Calls the AdminOnboardingController endpoints for managing
 * archetypes, milestones, archetype-milestone mappings, and simulation.
 *
 * DEV-505: Archetype Progression Designer
 * DEV-513: Archetype Progression Simulator
 *
 * English only (admin UI).
 */

import { apiFetch as clientFetch } from './client';

const API_PREFIX = '/api/admin/onboarding';

// ── Types ────────────────────────────────────────────────────

export interface ArchetypeResponse {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  defaultLandingRoute: string;
  defaultFeatures: string[];
  isSelfSelectable: boolean;
  minTrustLevelToInvite: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  milestones: ArchetypeMilestoneMapResponse[];
}

export interface ArchetypeMilestoneMapResponse {
  id: string;
  archetypeId: string;
  milestoneId: string;
  milestoneSlug: string;
  milestoneName: string;
  milestoneTriggerType: string;
  featuresUnlocked: string[];
  isPreCompleted: boolean;
  sortOrder: number;
  roleFilter: string | null;
}

export interface MilestoneResponse {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  triggerType: string;
  triggerConfig: string;
  nudgeTextKey: string | null;
  contentConfig: string | null;
  sortOrder: number;
  createdAt: string;
}

export interface FeatureFlagDefinition {
  key: string;
  description: string;
}

export interface SimulationStateResponse {
  archetypeSlug: string;
  milestones: SimulatedMilestoneState[];
  activeFeatures: string[];
  nudgeMessage: string | null;
}

export interface SimulatedMilestoneState {
  slug: string;
  name: string;
  isCompleted: boolean;
  featuresUnlocked: string[];
}

// ── API calls ────────────────────────────────────────────────
// Uses the shared apiFetch from client.ts which handles:
//   - API_BASE_URL (http://localhost:5295 in dev)
//   - Auth headers (Bearer JWT or X-User-Guid)
//   - Error parsing

function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  return clientFetch<T>(`${API_PREFIX}${path}`, options);
}

/** GET /archetypes — all archetypes with nested milestone maps. */
export function fetchArchetypes(): Promise<ArchetypeResponse[]> {
  return apiFetch('/archetypes');
}

/** GET /milestones — all milestone definitions. */
export function fetchMilestones(): Promise<MilestoneResponse[]> {
  return apiFetch('/milestones');
}

/** GET /feature-flags — static feature flag registry. */
export function fetchFeatureFlagRegistry(): Promise<FeatureFlagDefinition[]> {
  return apiFetch('/feature-flags');
}

/** POST /simulate — ephemeral progression simulation. */
export function simulateProgression(
  archetypeSlug: string,
  completedMilestoneSlugs: string[]
): Promise<SimulationStateResponse> {
  return apiFetch('/simulate', {
    method: 'POST',
    body: JSON.stringify({ archetypeSlug, completedMilestoneSlugs }),
  });
}

/** PUT /archetypes/{slug}/milestones — bulk save milestone mappings. */
export function saveArchetypeMilestones(
  slug: string,
  milestones: Array<{
    id?: string;
    milestoneId: string;
    featuresUnlocked: string[];
    isPreCompleted: boolean;
    sortOrder: number;
    roleFilter?: string | null;
  }>
): Promise<ArchetypeMilestoneMapResponse[]> {
  return apiFetch(`/archetypes/${slug}/milestones`, {
    method: 'PUT',
    body: JSON.stringify({ milestones }),
  });
}

/** POST /archetypes — create a new archetype. */
export function createArchetype(data: {
  slug: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  defaultLandingRoute: string;
  defaultFeatures: string[];
  isSelfSelectable: boolean;
  minTrustLevelToInvite?: string | null;
  isActive: boolean;
  sortOrder: number;
}): Promise<ArchetypeResponse> {
  return apiFetch('/archetypes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** PUT /archetypes/{slug} — update archetype properties. */
export function updateArchetype(slug: string, data: {
  name: string;
  description?: string | null;
  icon?: string | null;
  defaultLandingRoute: string;
  defaultFeatures: string[];
  isSelfSelectable: boolean;
  minTrustLevelToInvite?: string | null;
  isActive: boolean;
  sortOrder: number;
}): Promise<ArchetypeResponse> {
  return apiFetch(`/archetypes/${slug}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}
